import { getUserData } from "@/auth/getUserData";
import useMessagingFilters from "@/composables/useMessagingFilters";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
import { useChatsStore } from "@/store/useChatsStore";
import { useCustomersStore } from "@/store/useCustomersStore";
import { useContactsStore } from "@/store/useContactsStore";
import { StatusFilterNameType } from "@/types/filter.types";
import { InboxModuleType } from "@/types/inboxModule.type";
import { resolveStatusFilter } from "@/utils/v3/chat.utils";
import { ChatsListChat, ReadDto } from "@saysimple-platform/dtos";
import { Ref, ref } from "@vue/composition-api";
import { RequestQueryBuilder } from "@rewiko/crud-request";

export default function useStatusFilters(state) {
    const filteredUnreadChats: Ref<ReadDto[]> = ref([]);

    const { filters, getActiveFilter: activeFilter, setActiveFilter, enforcedTeamFilters } = useMessagingFilters();

    const switchStatusFilter = (chat: Pick<ChatsListChat, "assignedUserId" | "assignedTeamId" | "status">) => {
        const chatsStore = useChatsStore();

        // Active chat can be closed (null). This also results in no switch of status filters when you archive a chat (exactly what we want)
        if (!chatsStore.activeChat) {
            return;
        }

        const chatStatusFilter = resolveStatusFilter(chat);

        // Status filter will be reset when you search for the first time.
        if (!activeFilter.value.status) {
            return;
        }

        setActiveFilter({ status: chatStatusFilter, page: 1 });
    };

    const resolveUnreadChatStatusFilter = (chat: ReadDto): StatusFilterNameType[] => {
        if (
            enforcedTeamFilters.value.enabled &&
            !enforcedTeamFilters.value.teamIds.includes(chat.assignedTeamId) &&
            !enforcedTeamFilters.value.includeUnassignedChats &&
            (chat.assignedTeamId === null || chat.assignedUserId === null)
        ) {
            return [];
        }

        const status: StatusFilterNameType[] = [];

        if (!chat.assignedUserId && !chat.assignedTeamId && chat.status === "open") {
            status.push(StatusFilterNameType.NEW);
        }

        if (chat.assignedUserId && chat.assignedUserId === getUserData().id && chat.status === "open") {
            status.push(StatusFilterNameType.PERSONAL);
        }

        if (chat.assignedUserId && chat.status === "open") {
            status.push(StatusFilterNameType.OPEN);
        }

        const { customerSettings } = useCustomersStore();
        if (
            (!chat.assignedUserId || customerSettings.expandedTeamFilter) &&
            chat.assignedTeamId &&
            chat.status === "open"
        ) {
            status.push(StatusFilterNameType.TEAM);
        }

        return status;
    };

    const filterChannelsFn = (chat: ReadDto | ChatsListChat) =>
        filters.channels.length === 0 || filters.channels.includes(chat.channelId);
    const filterTagsFn = (chat: ReadDto | ChatsListChat) =>
        // @ts-ignore
        filters.tags.length === 0 || filters.tags.some((filteredTagId) => chat.tagIds.includes(filteredTagId));

    const filterTeamsFn = (chat: ReadDto | ChatsListChat) => {
        const teamsFilteredOn =
            filters.teams.length === 0 && enforcedTeamFilters.value.enabled
                ? enforcedTeamFilters.value.teamIds
                : filters.teams;

        const includedUnassignedChats =
            enforcedTeamFilters.value.includeUnassignedChats &&
            (chat.assignedTeamId === null || chat.assignedUserId === null);

        return includedUnassignedChats || teamsFilteredOn.length === 0 || teamsFilteredOn.includes(chat.assignedTeamId);
    };

    const filterLabelsFn = (chat: ReadDto | ChatsListChat) => {
        const contactsStore = useContactsStore();
        const contactLabels = contactsStore.getContactById(chat.contactId)?.labels;

        return (
            filters.labelIds.length === 0 ||
            contactLabels?.some((contactLabel) => filters.labelIds.includes(contactLabel.id))
        );
    };

    const filterUsersFn = (chat: ReadDto | ChatsListChat) =>
        filters.userIds.length === 0 || filters.userIds.includes(chat.assignedUserId);

    const filterSearchFn = () => {
        return !activeFilter.value.keyword;
    };

    const filterReadStatus = (chat: ReadDto | ChatsListChat) => {
        if (filters.read.length == 0) {
            return true;
        }
        if (chat instanceof ReadDto) {
            return filters.read.includes(chat.isChatRead ? 1 : 0);
        }
        return filters.read.includes(chat.read ? 1 : 0);
    };

    const filterUnreadChats = (inboxModule: InboxModuleType): ReadDto[] => {
        const { messaging, email, team } = useUnreadChats();

        const unreadChatsState =
            inboxModule === InboxModuleType.MESSAGING
                ? messaging.value
                : InboxModuleType.EMAIL
                ? email.value
                : team.value;

        const unreadChats = unreadChatsState.unreadChats
            .filter(filterChannelsFn)
            .filter(filterTeamsFn)
            .filter(filterTagsFn);

        filteredUnreadChats.value = unreadChats;
        return unreadChats;
    };

    return {
        resolveUnreadChatStatusFilter,
        filterUnreadChats,
        switchStatusFilter,
        filteredUnreadChats,
        statusFilters: state,

        // Filter functions
        filterChannelsFn,
        filterTeamsFn,
        filterTagsFn,
        filterSearchFn,
        filterLabelsFn,
        filterUsersFn,
        filterReadStatus,
    };
}
